import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { appClient } from "@/api/appClient";

export default function SetupCaseRoles() {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleAddSlotColumns = async () => {
    setIsProcessing(true);
    setResult(null);

    try {
      console.log("🔧 Adding slot columns to case_contacts...");
      const response = await appClient.functions.invoke('addSlotLabelColumn', {});

      console.log("✅ Response:", response.data);

      if (response.data.success) {
        setResult({
          success: true,
          message: response.data.message || "Columns added successfully!"
        });
      } else {
        setResult({
          success: false,
          message: response.data.error || "Failed to add columns"
        });
      }
    } catch (error) {
      console.error("❌ Error:", error);
      setResult({
        success: false,
        message: error.response?.data?.error || error.message
      });
    }

    setIsProcessing(false);
  };

  const handleSetupSchema = async () => {
    setIsProcessing(true);
    setResult(null);

    try {
      console.log("🔧 Setting up case roles schema...");
      const response = await appClient.functions.invoke('setupCaseRolesSchema', {});

      console.log("✅ Response:", response.data);

      if (response.data.success) {
        if (response.data.sql) {
          // Manual SQL execution needed
          setResult({
            success: true,
            message: response.data.message,
            sql: response.data.sql,
            note: response.data.note
          });
        } else {
          // Automated setup succeeded
          setResult({
            success: true,
            message: "Schema setup completed successfully!",
            objects: response.data.objects_created
          });
        }
      } else {
        setResult({
          success: false,
          message: response.data.error || "Failed to setup schema"
        });
      }
    } catch (error) {
      console.error("❌ Error:", error);
      setResult({
        success: false,
        message: error.response?.data?.error || error.message
      });
    }

    setIsProcessing(false);
  };

  return (
    <div className="bg-slate-50 p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(createPageUrl("Settings"))}
          className="mb-6 text-slate-600 flex items-center gap-2 hover:scale-105 transition-transform"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Setup Case Roles</h1>
          <p className="text-slate-600">
            Configure the case roles schema to enable advanced case management features
          </p>
        </div>

        <div className="space-y-6">
          {/* Step 1: Add Slot Columns */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Step 1: Add Slot Columns</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Add the necessary columns to the case_contacts table to support empty slots and slot assignments.
              </p>
              <Button
                onClick={handleAddSlotColumns}
                disabled={isProcessing}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding Columns...
                  </>
                ) : (
                  "Add Slot Columns"
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Step 2: Setup Schema */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Step 2: Setup Case Roles Schema (Optional)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Set up advanced case roles schema with enforcement functions and triggers.
              </p>
              <Button
                onClick={handleSetupSchema}
                disabled={isProcessing}
                variant="outline"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  "Setup Schema"
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Results */}
          {result && (
            <Card className={`border-2 ${result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  {result.success ? (
                    <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 space-y-3">
                    <p className={`font-semibold ${result.success ? 'text-green-900' : 'text-red-900'}`}>
                      {result.message}
                    </p>

                    {result.note && (
                      <p className="text-sm text-slate-700">{result.note}</p>
                    )}

                    {result.objects && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-slate-700 mb-2">Objects created:</p>
                        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                          {result.objects.map((obj, i) => (
                            <li key={i}>{obj}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {result.sql && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-slate-700 mb-2">
                          Please execute the following SQL in your Supabase SQL Editor:
                        </p>
                        <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs overflow-x-auto">
                          {result.sql}
                        </pre>
                        <Button
                          onClick={() => {
                            navigator.clipboard.writeText(result.sql);
                            alert("SQL copied to clipboard!");
                          }}
                          variant="outline"
                          size="sm"
                          className="mt-3"
                        >
                          Copy SQL
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}